<template>
  <div
    class="tw:bg-green-100 tw:rounded-t-lg tw:px-4 tw:py-[18px] tw:gap-y-2 tw:flex tw:flex-col tw:md:flex-row tw:justify-between"
    data-testid="hotel-offers-current-offer"
  >
    <div class="tw:flex tw:flex-row tw:items-baseline tw:gap-x-3">
      <p class="tw:font-bold tw:text-green-700 tw:text-sm">{{ $t('hotel_offers_status_live') }}</p>
    </div>
    <BaseLinkButton
      :href="`https://www.trivago.com/${platformsLanguages[language]}/srl?search=100-${selectedAccommodationId}`"
      rel="noopener noreferrer"
      target="_blank"
      @click="trackEvent"
      class="tw:flex tw:flex-row tw:items-center tw:text-blue-700 tw:hover:no-underline tw:font-bold tw:text-sm"
    >
      {{ $t('hotel_offers_show_trivago') }}
      <NewTab class="tw:fill-none tw:stroke-blue-700 tw:mb-1 tw:ml-2 tw:size-4" />
    </BaseLinkButton>
  </div>
  <div
    class="tw:bg-white tw:rounded-b-lg tw:p-4 tw:flex tw:flex-col tw:md:flex-row tw:md:items-center tw:justify-between tw:gap-y-2"
  >
    <div class="tw:flex tw:flex-row tw:gap-x-3 tw:items-center">
      <img class="tw:h-[40px] tw:w-[40px]" :src="hotelOfferIcon" alt="Hotel Offer" />
      <div class="tw:flex tw:gap-y-1 tw:flex-col">
        <p class="tw:font-bold tw:text-gray-800">
          {{ $t(`ho_${activeOffer?.template}_title`) }}
        </p>
        <p v-if="activeOffer?.benefitValue" class="tw:text-gray-700 tw:text-xs tw:leading-3">
          {{ `${$t('hotel_offers_discount')} ${discountFormatted}` }}
        </p>
        <p class="tw:text-gray-700 tw:text-xs tw:leading-3">
          {{ $t('hotel_offer_post_dated') }} {{ offerCreationDate }}
        </p>
        <p class="tw:text-gray-600 tw:text-xs" v-if="activeOffer?.expirationDate">
          {{ `${$t('hotel_offers_validity_date')} ${offerExpirationDate}` }}
        </p>
      </div>
    </div>
    <div class="tw:flex tw:justify-between tw:items-center tw:md:justify-end tw:md:gap-x-8">
      <button
        class="tw:text-xs tw:font-bold tw:text-gray-800 tw:hover:cursor-pointer tw:hover:text-blue-800"
        data-testid="hotel-offers-active-offer-cta-remove"
        @click="openRemoveModal"
      >
        {{ $t('hotel_offers_cta_remove') }}
      </button>
      <GhostButton @click="openEditOffer(activeOffer)" size="medium">{{ $t('hotel_offers_cta_update') }}</GhostButton>
    </div>
  </div>
</template>
<script setup>
import { computed } from 'vue'

import NewTab from '@/components/icons/NewTab.svg?component'
import GhostButton from '@/components/base/buttons/GhostButton.vue'
import BaseLinkButton from '@/components/base/buttons/BaseLinkButton.vue'

import { platformsLanguages } from '@/constants/platformLanguages.ts'
import { selectedAccommodationId } from '@/layouts/queries'
import { useOffersTemplates } from '@/components/hotelOffers/queries'
import { useSessionValues } from '@/composables/useSessionValues'
import { useFormatting } from '@/composables/useFormatting'

import appAnalytics from '@/utils/tracking'

const hotelOfferIcon = new URL(`../../assets/img/hotel-offers/icons/UI/offer.png`, import.meta.url)

const props = defineProps({
  activeOffer: Object,
  source: String,
})

const { formatDate, formatPercentage } = useFormatting()
const { templates } = useOffersTemplates()
const { language } = useSessionValues()
const trackEvent = () => {
  appAnalytics.track(appAnalytics.events.HO_SHOW_PROFILE_ON_TRIVAGO_CLICKED, {
    accommodationId: selectedAccommodationId.value,
    offerName: props.activeOffer?.template,
    source: props.source,
  })
}
const emit = defineEmits(['openRemoveModal', 'openEditOffer'])
const openRemoveModal = () => {
  appAnalytics.track(appAnalytics.events.HO_REMOVE_OFFER_CLICKED, {
    accommodationId: selectedAccommodationId.value,
    offerName: props.activeOffer?.template,
    source: props.source,
  })
  emit('openRemoveModal')
}
const openEditOffer = activeOffer => {
  appAnalytics.track(appAnalytics.events.HO_UPDATE_OFFER_CLICKED, {
    accommodationId: selectedAccommodationId.value,
    offerName: activeOffer?.template,
    source: props.source,
  })
  emit('openEditOffer', activeOffer, templates)
}

const offerCreationDate = computed(() => {
  if (!props.activeOffer?.publishingDate) return null

  const publishingDate = props.activeOffer?.publishingDate.replace('@', 'T')
  return formatDate(new Date(publishingDate), {
    timeZone: 'UTC',
  })
})

const offerExpirationDate = computed(() => {
  if (!props.activeOffer?.expirationDate) return null
  const dateFormat = props.activeOffer?.expirationDate.replace('@', 'T')
  return formatDate(new Date(dateFormat), {
    timeZone: 'UTC',
  })
})

const discountFormatted = computed(() => formatPercentage(props.activeOffer?.benefitValue, 0, 0))
</script>
